import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Mail, 
  Phone, 
  Building2, 
  Copy, 
  ExternalLink,
  Users,
  Hotel,
  Landmark,
  Cpu,
  Pill,
  Car,
  ShoppingBag,
  Zap,
  Search,
  Send
} from "lucide-react";

const contacts = {
  venues: [
    {
      name: "Meliá Sitges",
      type: "Hotel & Centro de Convenciones",
      email: "melia.sitges@melia.com",
      phone: "+34 938 11 08 11",
      address: "Joan Salvat Papasseit 38, 08870 Sitges",
      notes: "26 salas, auditorio hasta 1.400 personas",
      priority: "high"
    },
    {
      name: "Sitges Convention Bureau",
      type: "Agencia MICE Oficial",
      email: "info@sitgescb.cat",
      email2: "sanchezqmr@sitges.cat",
      phone: "+34 93 810 93 10",
      website: "sitgescb.cat",
      notes: "Contacto principal para eventos MICE en Sitges",
      priority: "high"
    },
    {
      name: "Hotel Dolce Sitges",
      type: "Resort 5* Congresos",
      email: null,
      phone: null,
      address: "Camí de Miralpeix 12, Sitges",
      notes: "Capacidad hasta 1.400 personas",
      priority: "medium"
    },
    {
      name: "4 Corporates DMC",
      type: "DMC Sitges/Barcelona",
      email: null,
      phone: "+34 936 94 00 78",
      website: "4corporates.com",
      notes: "Gestión de eventos MICE",
      priority: "medium"
    }
  ],
  tech: [
    {
      name: "SAP",
      type: "Software Empresarial",
      email: "delia.sieff@sap.com",
      email2: "monika.schaller@sap.com",
      notes: "Head EMEA / Chief Communications",
      events: ["SAP Partner Summit", "SAP Sapphire"],
      priority: "high"
    },
    {
      name: "Microsoft",
      type: "Tecnología",
      email: null,
      website: "microsoft.com/en/emea/partners-events",
      notes: "Formulario web para eventos EMEA",
      events: ["Microsoft Inspire"],
      priority: "high"
    },
    {
      name: "Salesforce",
      type: "CRM & Cloud",
      email: null,
      phone: "1-800-664-9073",
      website: "salesforce.com/events/sponsorship",
      notes: "Contacto de sponsorship",
      events: ["Salesforce World Tour"],
      priority: "high"
    },
    {
      name: "Cisco",
      type: "Networking",
      email: "CiscoLiveEMEA@cisco.com",
      notes: "Email oficial para Cisco Live EMEA",
      events: ["Cisco Live EMEA"],
      priority: "high"
    },
    {
      name: "IBM España",
      type: "Tecnología",
      email: "jorge.mosquera@es.ibm.com",
      email2: "alvaro.cg@ibm.com",
      address: "Plaza Pablo Ruiz Picasso 11, 28020 Madrid",
      notes: "Contacto Think on Tour Madrid",
      events: ["IBM Think Barcelona"],
      priority: "high"
    },
    {
      name: "Oracle España",
      type: "Base de datos & Cloud",
      email: null,
      phone: "+34 916 036 188",
      notes: "Contacto general España",
      events: ["Oracle Cloud Conference"],
      priority: "medium"
    },
    {
      name: "Google Cloud",
      type: "Cloud Computing",
      email: null,
      website: "cloud.google.com/contact",
      notes: "Formulario de contacto",
      events: ["Google Cloud Summit"],
      priority: "medium"
    },
    {
      name: "AWS",
      type: "Cloud Computing",
      email: null,
      website: "aws.amazon.com/events/summits",
      notes: "AWS Summit Madrid - Junio 2026",
      events: ["AWS Summit Barcelona"],
      priority: "medium"
    }
  ],
  pharma: [
    {
      name: "Novartis",
      type: "Farmacéutica",
      email: null,
      website: "novartis.com/events",
      notes: "Annual Meeting típicamente en Meliá Sitges",
      events: ["Novartis Annual Meeting Sitges"],
      priority: "high"
    },
    {
      name: "Roche España",
      type: "Farmacéutica",
      email: null,
      phone: "+34 913 248 100",
      address: "C De La Ribera Del Loira 50, Madrid",
      notes: "Solo usar dominios @roche.com oficiales",
      events: ["Roche Pharma Conference"],
      priority: "high"
    },
    {
      name: "Pfizer España",
      type: "Farmacéutica",
      email: null,
      website: "pfizer.es",
      notes: "Evento anual en Barcelona ~1.800 profesionales",
      events: ["Pfizer European Summit"],
      priority: "high"
    },
    {
      name: "Johnson & Johnson",
      type: "Healthcare",
      email: null,
      website: "jnj.com",
      events: ["J&J Medical Devices"],
      priority: "medium"
    },
    {
      name: "AstraZeneca",
      type: "Farmacéutica",
      email: null,
      website: "astrazeneca.com",
      events: ["AstraZeneca Oncology Forum"],
      priority: "medium"
    }
  ],
  automotive: [
    {
      name: "SEAT/CUPRA",
      type: "Automoción",
      email: null,
      website: "seat.com / cupraofficial.com",
      address: "CUPRA HQ, Barcelona",
      notes: "Convención anual de concesionarios",
      events: ["SEAT/Cupra Dealer Convention"],
      priority: "high"
    },
    {
      name: "BMW Group",
      type: "Automoción",
      email: "info@visit-bmwgroup.com",
      email2: "ir@bmwgroup.com",
      notes: "Eventos corporativos / Investor Relations",
      events: ["BMW Group Executive Meeting"],
      priority: "high"
    },
    {
      name: "Mercedes-Benz España",
      type: "Automoción",
      email: "dialog@mercedes-benz.com",
      email2: "fernando.rumoroso@daimler.com",
      phone: "+34 914 846 000",
      address: "Avda. de Bruselas 30, 28108 Alcobendas",
      notes: "Fleet contact / Renting",
      events: ["Mercedes-Benz Fleet Conference"],
      priority: "high"
    }
  ],
  finance: [
    {
      name: "Banco Santander",
      type: "Banca",
      email: null,
      website: "santander.com/shareholders-and-investors/corporate-events",
      address: "Ciudad Grupo Santander, Boadilla del Monte",
      notes: "SIBC Conference anual en octubre",
      events: ["Santander Global Management"],
      priority: "high"
    },
    {
      name: "BBVA",
      type: "Banca",
      email: null,
      website: "bbva.com",
      notes: "Digital Summit con fintechs",
      events: ["BBVA Digital Summit"],
      priority: "medium"
    },
    {
      name: "Allianz",
      type: "Seguros",
      email: null,
      website: "allianz.com",
      events: ["Allianz Insurance Forum"],
      priority: "medium"
    }
  ],
  consumer: [
    {
      name: "Nestlé España",
      type: "Alimentación",
      email: "mediarelations@nestle.com",
      phone: "+41 21 924 2200",
      address: "Avd. Paisos Catalans 25-51, 08950 Esplugues, Barcelona",
      notes: "Media relations global",
      events: ["Nestlé European Leadership"],
      priority: "high"
    },
    {
      name: "L'Oréal",
      type: "Cosmética",
      email: "contact@loreal.com",
      address: "546 Calle Alcalá, Madrid",
      notes: "Contacto global corporativo",
      events: ["L'Oréal Innovation Days"],
      priority: "medium"
    },
    {
      name: "Inditex/Zara",
      type: "Retail/Moda",
      email: "inditexinfo@inditex.com",
      email2: "press@inditex.com",
      phone: "+34 981 185 400",
      address: "Arteixo, A Coruña",
      notes: "Info general / Prensa",
      events: ["Inditex Supplier Meeting"],
      priority: "high"
    },
    {
      name: "Unilever",
      type: "Consumo",
      email: null,
      website: "unilever.com",
      events: ["Unilever Brand Summit"],
      priority: "medium"
    }
  ],
  energy: [
    {
      name: "Iberdrola",
      type: "Energía",
      email: null,
      website: "iberdrola.com",
      events: ["Iberdrola Renewables Summit"],
      priority: "medium"
    },
    {
      name: "Repsol",
      type: "Energía",
      email: null,
      website: "repsol.com",
      events: ["Repsol Energy Transition Forum"],
      priority: "medium"
    }
  ]
};

const categoryInfo = {
  venues: { label: "Venues & MICE", icon: Hotel, color: "text-blue-600" },
  tech: { label: "Tecnológicas", icon: Cpu, color: "text-purple-600" },
  pharma: { label: "Farmacéuticas", icon: Pill, color: "text-green-600" },
  automotive: { label: "Automoción", icon: Car, color: "text-red-600" },
  finance: { label: "Banca & Finanzas", icon: Landmark, color: "text-amber-600" },
  consumer: { label: "Consumo & Retail", icon: ShoppingBag, color: "text-pink-600" },
  energy: { label: "Energía", icon: Zap, color: "text-yellow-600" }
};

const MICEContacts = () => {
  const [activeTab, setActiveTab] = useState("venues");
  const [searchTerm, setSearchTerm] = useState("");

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copiado: ${text}`);
  };

  const copyAllEmails = (category) => {
    const emails = contacts[category]
      .filter(c => c.email)
      .map(c => c.email)
      .join(", ");
    if (emails) {
      navigator.clipboard.writeText(emails);
      toast.success(`${emails.split(",").length} emails copiados`);
    } else {
      toast.error("No hay emails disponibles en esta categoría");
    }
  };

  const filteredContacts = (category) => {
    if (!searchTerm) return contacts[category];
    return contacts[category].filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.events?.some(e => e.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const totalEmails = Object.values(contacts).flat().filter(c => c.email).length;

  const ContactCard = ({ contact }) => {
    const Icon = categoryInfo[activeTab]?.icon || Building2;
    
    return (
      <Card className={`${contact.priority === "high" ? "border-l-4 border-l-blue-500" : ""}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{contact.name}</h3>
                {contact.priority === "high" && (
                  <Badge className="bg-blue-100 text-blue-800 text-xs">Prioritario</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-2">{contact.type}</p>
              
              <div className="space-y-1">
                {contact.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span className="font-mono text-blue-600">{contact.email}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(contact.email)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {contact.email2 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="font-mono text-gray-600">{contact.email2}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(contact.email2)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-green-500" />
                    <span>{contact.phone}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => copyToClipboard(contact.phone)}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {contact.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <ExternalLink className="w-4 h-4 text-purple-500" />
                    <a href={`https://${contact.website}`} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                      {contact.website}
                    </a>
                  </div>
                )}
                {contact.address && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    <span>{contact.address}</span>
                  </div>
                )}
              </div>
              
              {contact.notes && (
                <p className="text-xs text-muted-foreground mt-2 italic">{contact.notes}</p>
              )}
              
              {contact.events && contact.events.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {contact.events.map((event, idx) => (
                    <Badge key={`event-${contact.id || contact.name}-${idx}`} variant="outline" className="text-xs">{event}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mice-contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contactos MICE</h1>
          <p className="text-muted-foreground">Empresas y venues para captar eventos corporativos ({totalEmails} emails)</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input 
            placeholder="Buscar empresa, evento..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(categoryInfo).map(([key, info]) => {
          const Icon = info.icon;
          const count = contacts[key]?.length || 0;
          const emailCount = contacts[key]?.filter(c => c.email).length || 0;
          return (
            <Card key={key} className={`cursor-pointer hover:border-primary/50 ${activeTab === key ? "border-primary" : ""}`} onClick={() => setActiveTab(key)}>
              <CardContent className="p-3 text-center">
                <Icon className={`w-5 h-5 mx-auto mb-1 ${info.color}`} />
                <p className="text-lg font-bold">{count}</p>
                <p className="text-xs text-muted-foreground">{info.label}</p>
                <p className="text-xs text-blue-600">{emailCount} emails</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList className="flex-wrap h-auto">
            {Object.entries(categoryInfo).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <TabsTrigger key={key} value={key} className="flex items-center gap-1">
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{info.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          <Button size="sm" variant="outline" onClick={() => copyAllEmails(activeTab)}>
            <Copy className="w-4 h-4 mr-2" />
            Copiar Emails
          </Button>
        </div>

        {Object.keys(categoryInfo).map(category => (
          <TabsContent key={category} value={category} className="space-y-3">
            {filteredContacts(category).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredContacts(category).map((contact) => (
                  <ContactCard key={contact.id || contact.name} contact={contact} />
                ))}
              </div>
            ) : (
              <Card className="bg-muted/50">
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No se encontraron contactos</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Tips */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800 dark:text-blue-300 flex items-center gap-2">
            <Send className="w-5 h-5" />
            Consejos para captar eventos
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-blue-900 dark:text-blue-200">
          <p>1. <strong>Sitges Convention Bureau</strong> es tu mejor aliado - conocen los eventos planificados</p>
          <p>2. <strong>Meliá Sitges</strong> - colabora para overflow cuando tienen eventos grandes</p>
          <p>3. Contacta con 3-6 meses de antelación para eventos corporativos</p>
          <p>4. Ofrece tarifas especiales para grupos y estancias largas</p>
          <p>5. Destaca la proximidad a Barcelona (30 min) y las vistas al mar</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MICEContacts;
